/* eslint-disable react/jsx-no-comment-textnodes */
import {forwardRef, lazy, Suspense, useMemo, useState} from "react";
import useForwardedRef from "../hooks/useForwardedRef";
import {PROJECT_THEME} from "../theme";
import {CodeBlock, createAnimatedCodeBlock} from "../component/Code";
import {Terminal, TerminalCommand, TerminalMessage} from "../component/Terminal";
import Equalizer from "../component/Equaliser";
import ServiceAnimation from "../component/serviceAnimations/ServiceAnimation";
import { beeswarm, keyCrack, mainframeJcl, pinMux, presenceCursors, wireframe } from "../component/serviceAnimations/home";
import { attentionFlow, TRANSFORMER_TOKENS } from "../component/serviceAnimations/scenesAttention";
import AnsiblePlaybookText from "../component/serviceAnimations/AnsiblePlaybookText";

const AnimalClassification = lazy(() => import("../component/AnimalClassification"));
const ComplianceAnimation = lazy(() => import("../component/ComplianceAnimation"));

export interface Service {
    title: string;
    shortDescription: string;
    fullDescription: string;
    relatedArticles?: Array<{ title: string; url: string }>;
    /* Falls back to "Start a project" when unset. */
    ctaText?: string;
    tags: string[];
    images?: React.ReactElement[];
    ServiceCardContent?: any
    ExpandedServiceCardContent?: any
    /* Superseded card content kept until the canvas animations are signed off. */
    LegacyCardContent?: any
}

const serviceTag = (filename: string) => `/assets/img/${filename}`;

// Web,
// Graphics & GPU Compute
// Cloud & Platform, Enterprise Applications
// Machine Learning, Embedded & Systems
// CyberSecurity, Data Science

// TODO nested service datastructure is not right
const SERVICES: Service[] = [
    {
        title: 'Web',
        shortDescription: 'Product-focused web experiences for SaaS, internal tools, and customer-facing platforms.',
        fullDescription: 'We build modern web products that balance sharp UX, accessibility, and long-term maintainability. From single-page applications and design systems to API integrations and launch-ready front ends, our work helps teams ship quickly without sacrificing reliability.',
        ctaText: "Let's Cook",
        relatedArticles: [
            { title: 'RAG - Need Some Context!', url: '/articles/rag' },
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Platform Modernization Program', url: '/articles/platform-modernization-program' },
            { title: 'Designing for Reliable Delivery', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('typescript.svg'),
            serviceTag('javascript.svg'),
            serviceTag('angular-icon-svgrepo-com.svg'),
            serviceTag('vue-svgrepo-com.svg'),
            serviceTag('php.svg'),
            serviceTag('python.svg'),
            serviceTag('node-sass-svgrepo-com.svg'),
            serviceTag('tailwindcss-icon-svgrepo-com.svg'),
            serviceTag('jquery-svgrepo-com.svg'),
            serviceTag('react-native.svg'),
            serviceTag('postgresql.svg'),
            serviceTag('mongo-svgrepo-com.svg'),
            serviceTag('mysql-svgrepo-com.svg'),
            serviceTag('cloudflare.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('postman-icon-svgrepo-com.svg'),
            serviceTag('figma-svgrepo-com.svg'),
            serviceTag('aws.svg'),
            serviceTag('git-icon-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={presenceCursors} interactive/>,
        LegacyCardContent: forwardRef<number, {}>((_, ref) => {

            const [characters, setCharacters] = useState(0)

            const {
                Keyword,
                Operator,
                Typename,
                Variable,
                Comment,
                RedactedComment,
                RedactedVariable,
                RedactedTemplateParam,
                RedactedTypename,
                CodeBlock
            } = createAnimatedCodeBlock(
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID_DARK,
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID,
                PROJECT_THEME.colour.COLOUR_LIGHT,
                PROJECT_THEME.colour.COLOUR_MID_LIGHT,
                characters,
                60,
                100,
                setCharacters
            )

            return (
                <CodeBlock>
                    <Comment>
                        //
                    </Comment>
                    <RedactedComment>
                        SomeComment
                    </RedactedComment>
                    <RedactedComment>
                        Another
                    </RedactedComment>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Keyword>
                        &emsp;typename
                    </Keyword>
                    <RedactedTypename style={{marginRight: '0'}}>
                        sometypename
                    </RedactedTypename>
                    <Operator>
                        ,
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Keyword>
                        typename
                    </Keyword>
                    <RedactedTypename>
                        typename
                    </RedactedTypename>
                    <Operator>
                        =
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        std
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        enable_if_t
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        is_arithmetic_v
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        +(
                    </Operator>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Keyword>
                        return
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (*
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator>
                        ) +
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        &#123;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        some
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#125;;
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                    <br/>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Typename>
                        &emsp;size_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        sometypename
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Typename>
                        uint64_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        typename123
                    </RedactedVariable>
                    <br/>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        *(
                    </Operator>
                    <Keyword>
                        const
                    </Keyword>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <Operator style={{marginRight: '2px'}}>
                        &
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Variable style={{marginRight: '0'}}>
                        &emsp;&emsp;std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ),
                    </Operator>
                    <br/>
                    <Variable style={{marginRight: '0'}}>
                        &emsp;&emsp;std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        anotherpara
                    </RedactedTemplateParam>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        alongerpara
                    </RedactedTemplateParam>
                    <Operator>
                        )
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&gt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#123;&#125;;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        =
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        &emsp;&emsp;static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        decltype
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        )&gt;(
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        -&gt;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        ) *
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;&emsp;static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        long double
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variable
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator>
                        ) /
                    </Operator>
                    <br/>
                    <Operator>
                        &emsp;&emsp;&emsp;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        somethingprettylong
                    </RedactedTemplateParam>
                    <br/>
                    <Operator>
                        &emsp;&emsp;&emsp;)
                    </Operator>
                    <br/>
                    <Operator>
                        &emsp;&emsp;);
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                </CodeBlock>
            )
        }),
        ExpandedServiceCardContent: forwardRef<number, {}>((_, ref) => {

            const [characters, setCharacters] = useState(0)

            const {
                Comment,
                Keyword,
                Variable,
                Operator,
                Typename,
                RedactedComment,
                RedactedVariable,
                RedactedTemplateParam,
                RedactedTypename,
                CodeBlock
            } = createAnimatedCodeBlock(
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID_DARK,
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID,
                PROJECT_THEME.colour.COLOUR_LIGHT,
                PROJECT_THEME.colour.COLOUR_MID_LIGHT,
                characters,
                80,
                80,
                setCharacters
            )

            return (
                <CodeBlock>
                    <Comment>
                        //
                    </Comment>
                    <RedactedComment>
                        SomeComment
                    </RedactedComment>
                    <RedactedComment>
                        Another
                    </RedactedComment>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Keyword>
                        typename
                    </Keyword>
                    <RedactedTypename style={{marginRight: '0'}}>
                        sometypename
                    </RedactedTypename>
                    <Operator>
                        ,
                    </Operator>
                    <Keyword>
                        typename
                    </Keyword>
                    <RedactedTypename>
                        typename
                    </RedactedTypename>
                    <Operator>
                        =
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        std
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        enable_if_t
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        is_arithmetic_v
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        +(
                    </Operator>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Keyword>
                        return
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (*
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator>
                        ) +
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        &#123;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        some
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#125;;
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                    <br/>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Typename>
                        size_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        sometypename
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <Typename>
                        uint64_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        typename123
                    </RedactedVariable>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        *(
                    </Operator>
                    <Keyword>
                        const
                    </Keyword>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <Operator style={{marginRight: '2px'}}>
                        &
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ),
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        anotherpara
                    </RedactedTemplateParam>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        alongerpara
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        )
                    </Operator>
                    <Operator>
                        &gt;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#123;&#125;;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable>
                        data
                    </RedactedVariable>
                    <Operator>
                        =
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        decltype
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        )&gt;(
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        -&gt;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        ) *
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        long double
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variable
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator>
                        ) /
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        somethingprettylong
                    </RedactedTemplateParam>
                    <Operator>
                        )
                    </Operator>
                    <br/>
                    <Operator>
                        &emsp;);
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                </CodeBlock>
            )
        })
    },
    {
        title: 'Enterprise Applications',
        shortDescription: 'Reliable software for complex operations, integrations, and business workflows.',
        fullDescription: 'We design and deliver enterprise systems that connect finance, operations, and customer data through dependable services and clear domain boundaries. The emphasis is on maintainability, auditability, and keeping critical workflows stable as organisations grow.',
        ctaText: 'Untangle The Mess',
        relatedArticles: [
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Network Security Foundations for Cloud-Native Systems', url: '/articles/network-security-foundations' },
            { title: 'Platform Modernization Program', url: '/articles/platform-modernization-program' },
            { title: 'Delivery Patterns for Complex Systems', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('java-svgrepo-com.svg'),
            serviceTag('kotlin-svgrepo-com.svg'),
            serviceTag('spring-boot.svg'),
            serviceTag('apache-camel-svgrepo-com.svg'),
            serviceTag('kafka.svg'),
            serviceTag('rabbitmq-icon-svgrepo-com.svg'),
            serviceTag('postgresql.svg'),
            serviceTag('mysql-svgrepo-com.svg'),
            serviceTag('mariadb.svg'),
            serviceTag('mongo-svgrepo-com.svg'),
            serviceTag('redis-svgrepo-com(1).svg'),
            serviceTag('elasticsearch.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('aws.svg'),
            serviceTag('azure.svg'),
            serviceTag('gcp.svg'),
            serviceTag('terraform.svg'),
            serviceTag('openshift-svgrepo-com.svg'),
            serviceTag('openstack-icon-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={mainframeJcl}/>,
        ExpandedServiceCardContent: forwardRef<number, {}>((_, ref) => {

            const forwardedRef = useForwardedRef(ref)

            return (
                <Terminal
                    ref={ref}
                    startMessage={forwardedRef.current ?? 1}
                    averageMessageSpeed={1000}
                    messageSpeedStandardDeviance={700}
                    style={{
                        height: '100%'
                    }}
                >
                    <TerminalCommand>
                        terraform apply --auto-approve
                    </TerminalCommand>
                    <TerminalMessage>
                        Terraform used the selected providers to generate the following execution plan. Resource
                        actions are indicated with the following symbols:
                        <br/>
                        + create
                        <br/>
                        Terraform will perform the following actions:
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            # aws_instance.oneill-devops-instance
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_instance" "oneill-devops-instance" {'}
                        <br/>
                        &emsp;{'+ ami                                  = "ami-07c1b39b7b3d2525d"'}
                        <br/>
                        &emsp;{'+ arn                                  = (known after apply)'}
                        <br/>
                        &emsp;{'+ associate_public_ip_address          = (known after apply)'}
                        <br/>
                        &emsp;{'+ availability_zone                    = (known after apply)'}
                        <br/>
                        &emsp;{'+ cpu_core_count                       = (known after apply)'}
                        <br/>
                        &emsp;{'+ cpu_threads_per_core                 = (known after apply)'}
                        <br/>
                        &emsp;{'+ disable_api_stop                     = (known after apply)'}
                        <br/>
                        &emsp;{'+ disable_api_termination              = (known after apply)'}
                        <br/>
                        &emsp;{'+ ebs_optimized                        = (known after apply)'}
                        <br/>
                        &emsp;{'+ get_password_data                    = false'}
                        <br/>
                        &emsp;{'+ host_id                              = (known after apply)'}
                        <br/>
                        &emsp;{'+ host_resource_group_arn              = (known after apply)'}
                        <br/>
                        &emsp;{'+ iam_instance_profile                 = (known after apply)'}
                        <br/>
                        &emsp;{'+ instance_lifecycle                   = (known after apply)'}
                        <br/>
                        &emsp;{'+ instance_initiated_shutdown_behavior = (known after apply)'}
                        <br/>
                        &emsp;{'+ instance_state                       = (known after apply)'}
                        <br/>
                        &emsp;{'+ instance_type                        = "t2.medium"'}
                        <br/>
                        &emsp;{'+ ipv6_address_count                   = (known after apply)'}
                        <br/>
                        &emsp;{'+ ipv6_addresses                       = (known after apply)'}
                        <br/>
                        &emsp;{'+ key_name                             = "oneill"'}
                        <br/>
                        &emsp;{'+ monitoring                           = (known after apply)'}
                        <br/>
                        &emsp;{'+ outpost_arn                          = (known after apply)'}
                        <br/>
                        &emsp;{'+ password_data                        = (known after apply)'}
                        <br/>
                        &emsp;{'+ placement_group                      = (known after apply)'}
                        <br/>
                        &emsp;{'+ placement_partition_number           = (known after apply)'}
                        <br/>
                        &emsp;{'+ primary_network_interface_id         = (known after apply)'}
                        <br/>
                        &emsp;{'+ private_dns                          = (known after apply)'}
                        <br/>
                        &emsp;{'+ private_ip                           = (known after apply)'}
                        <br/>
                        &emsp;{'+ public_dns                           = (known after apply)'}
                        <br/>
                        &emsp;{'+ public_ip                            = (known after apply)'}
                        <br/>
                        &emsp;{'+ security_groups                      = ['}
                        <br/>
                        &emsp;&emsp;{'+ "oneill-devops-sg",'}
                        <br/>
                        &emsp;{']'}
                        <br/>
                        &emsp;{'+ tags                                 = {'}
                        <br/>
                        &emsp;&emsp;{'+ "lifecycle" = "transient"'}
                        <br/>
                        &emsp;{'}'}
                        <br/>
                        &emsp;{'+ tenancy                              = (known after apply)'}
                        <br/>
                        &emsp;{'+ user_data                            = "d9f1acd8dc0a74a141fd806cf80e8fe816a9afa7"'}
                        <br/>
                        &emsp;{'+ user_data_base64                     = (known after apply)'}
                        <br/>
                        &emsp;{'+ user_data_replace_on_change          = false'}
                        <br/>
                        &emsp;{'+ vpc_security_group_ids               = (known after apply)'}
                        <br/>
                        <br/>
                        &emsp;{'+ root_block_device {'}
                        <br/>
                        &emsp;&emsp;{'+ delete_on_termination = true'}
                        <br/>
                        &emsp;&emsp;{'+ device_name           = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ encrypted             = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ iops                  = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ kms_key_id            = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ tags_all              = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ throughput            = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ volume_id             = (known after apply)'}
                        <br/>
                        &emsp;&emsp;{'+ volume_size           = 50'}
                        <br/>
                        &emsp;&emsp;{'+ volume_type           = (known after apply)'}
                        <br/>
                        &emsp;{'}'}
                        <br/>
                        {'}'}
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            # aws_security_group.oneill-devops-sg
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_security_group" "oneill-devops-sg" {'}
                        <br/>
                        &emsp;{'+ arn                    = (known after apply)'}
                        <br/>
                        &emsp;{'+ description            = "Allow inbound traffic on port 80 and 22 from approved cidr block"'}
                        <br/>
                        &emsp;{'+ egress                 = ['}
                        <br/>
                        &emsp;&emsp;{'+ {'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ cidr_blocks      = ['}
                        <br/>
                        &emsp;&emsp;&emsp;&emsp;{'+ "0.0.0.0/0",'}
                        <br/>
                        &emsp;&emsp;&emsp;{']'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ description      = ""'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ from_port        = 0'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ ipv6_cidr_blocks = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ prefix_list_ids  = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ protocol         = "-1"'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ security_groups  = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ self             = false'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ to_port          = 0'}
                        <br/>
                        &emsp;&emsp;{'},'}
                        <br/>
                        &emsp;{']'}
                        <br/>
                        &emsp;{'+ id                     = (known after apply)'}
                        <br/>
                        &emsp;{'+ ingress                = ['}
                        <br/>
                        &emsp;&emsp;{'+ {'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ cidr_blocks      = ['}
                        <br/>
                        &emsp;&emsp;&emsp;&emsp;{'+ "0.0.0.0/0"'}
                        <br/>
                        &emsp;&emsp;&emsp;{']'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ description      = ""'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ from_port        = 80'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ ipv6_cidr_blocks = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ prefix_list_ids  = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ protocol         = "tcp"'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ security_groups  = []'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ self             = false'}
                        <br/>
                        &emsp;&emsp;&emsp;{'+ to_port          = 80'}
                        <br/>
                        &emsp;&emsp;{'},'}
                        <br/>
                        &emsp;{']'}
                        <br/>
                        &emsp;{'+ name                   = "oneill-devops-sg"'}
                        <br/>
                        &emsp;{'+ name_prefix            = (known after apply)'}
                        <br/>
                        &emsp;{'+ owner_id               = (known after apply)'}
                        <br/>
                        &emsp;{'+ revoke_rules_on_delete = false'}
                        <br/>
                        &emsp;{'+ vpc_id                 = "vpc-0e6cb979ca4de002b"'}
                        <br/>
                        &emsp;{'}'}
                        <br/>
                        <br/>
                        Plan: 2 to add, 0 to change, 0 to destroy.
                    </TerminalMessage>
                    <br/>
                    <TerminalMessage>
                        <strong>
                            # aws_lambda_function.oneill-devops-lambda
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_lambda_function" "oneill-devops-lambda" {'}
                        <br/>
                        &emsp;{'+ function_name    = "oneill-devops-lambda"'}
                        <br/>
                        &emsp;{'+ role             = (known after apply)'}
                        <br/>
                        &emsp;{'+ handler          = "index.handler"'}
                        <br/>
                        &emsp;{'+ runtime          = "nodejs18.x"'}
                        <br/>
                        &emsp;{'+ source_code_hash = (known after apply)'}
                        <br/>
                        &emsp;{'+ memory_size      = 512'}
                        <br/>
                        &emsp;{'+ timeout          = 15'}
                        <br/>
                        {'}'}
                    </TerminalMessage>

                    <TerminalMessage>
                        <strong>
                            # aws_s3_bucket.oneill-devops-bucket
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_s3_bucket" "oneill-devops-bucket" {'}
                        <br/>
                        &emsp;{'+ bucket = "oneill-devops-bucket"'}
                        <br/>
                        &emsp;{'+ acl    = "private"'}
                        <br/>
                        {'}'}
                    </TerminalMessage>

                    <TerminalMessage>
                        <strong>
                            # aws_iam_role.oneill-devops-role
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_iam_role" "oneill-devops-role" {'}
                        <br/>
                        &emsp;{'+ name               = "oneill-devops-role"'}
                        <br/>
                        &emsp;{'+ assume_role_policy = (known after apply)'}
                        <br/>
                        {'}'}
                    </TerminalMessage>

                    <TerminalMessage>
                        <strong>
                            # aws_dynamodb_table.oneill-devops-table
                        </strong>
                        {" will be created"}
                        <br/>
                        {'+ resource "aws_dynamodb_table" "oneill-devops-table" {'}
                        <br/>
                        &emsp;{'+ name           = "oneill-devops-table"'}
                        <br/>
                        &emsp;{'+ billing_mode   = "PAY_PER_REQUEST"'}
                        <br/>
                        &emsp;{'+ hash_key       = "id"'}
                        <br/>
                        &emsp;{'+ attribute      = [{"key": "id", "type" : "S"}]'}
                        <br/>
                        {'}'}
                    </TerminalMessage>

                    <TerminalMessage>
                        Plan: 4 to add, 0 to change, 0 to destroy.
                    </TerminalMessage>

                    <TerminalMessage>
                        Changes to Outputs:
                        <br/>
                        &emsp;{'+ lambda_function_arn = (known after apply)'}
                        <br/>
                        &emsp;{'+ s3_bucket_name = "oneill-devops-bucket"'}
                        <br/>
                        &emsp;{'+ iam_role_arn = (known after apply)'}
                        <br/>
                        &emsp;{'+ dynamodb_table_name = "oneill-devops-table"'}
                    </TerminalMessage>

                    <TerminalMessage>
                        <strong>
                            aws_lambda_function.oneill-devops-lambda: Creating...
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_lambda_function.oneill-devops-lambda: Creation complete after 5s
                            [id=oneill-devops-lambda]
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_s3_bucket.oneill-devops-bucket: Creating...
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_s3_bucket.oneill-devops-bucket: Creation complete after 3s
                            [id=oneill-devops-bucket]
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_dynamodb_table.oneill-devops-table: Creating...
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_dynamodb_table.oneill-devops-table: Creation complete after 6s
                            [id=oneill-devops-table]
                        </strong>
                    </TerminalMessage>

                    <TerminalMessage>
                        <br/>
                        <br/>
                        <strong style={{color: PROJECT_THEME.colour.COLOUR_DARK}}>
                            Apply complete! Resources: 4 added, 0 changed, 0 destroyed.
                        </strong>
                        <br/>
                        <br/>
                        <strong style={{color: PROJECT_THEME.colour.COLOUR_DARK}}>
                            Outputs:
                        </strong>
                        <br/>
                        <br/>
                        lambda_function_arn =
                        "arn:aws:lambda:us-west-2:123456789012:function:oneill-devops-lambda"
                        <br/>
                        s3_bucket_name = "oneill-devops-bucket"
                        <br/>
                        iam_role_arn = "arn:aws:iam::123456789012:role/oneill-devops-role"
                        <br/>
                        dynamodb_table_name = "oneill-devops-table"
                    </TerminalMessage>
                    <br/>
                    <TerminalMessage>
                        Changes to Outputs:
                        <br/>
                        &emsp;{'+ web-service-url = (known after apply)'}
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_security_group.oneill-devops-sg: Creating...
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_security_group.oneill-devops-sg: Creation complete after 3s
                            [id=sg-07fbe63767878df45]
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_instance.oneill-devops-instance: Creating...
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_instance.oneill-devops-instance: Still creating... [10s elapsed]
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <strong>
                            aws_instance.oneill-devops-instance: Creation complete after 12s
                            [id=i-08b1ddf6b62890b86]
                        </strong>
                    </TerminalMessage>
                    <TerminalMessage>
                        <br/>
                        <br/>
                        <strong style={{color: PROJECT_THEME.colour.COLOUR_DARK}}>
                            Apply complete! Resources: 2 added, 0 changed, 0 destroyed.
                        </strong>
                        <br/>
                        <br/>
                        <strong style={{color: PROJECT_THEME.colour.COLOUR_DARK}}>
                            Outputs:
                        </strong>
                        <br/>
                        <br/>
                        web-service-url = "http://ec2-13-40-27-100.eu-west-2.compute.amazonaws.com"
                    </TerminalMessage>
                </Terminal>
            )
        })
    },
    {
        title: 'Cloud & Platform',
        shortDescription: 'Cloud-native platforms, infrastructure automation, and resilient release paths.',
        fullDescription: 'We help teams build platform foundations that make cloud complexity manageable. From infrastructure as code and deployment pipelines to observability and service ownership, we create environments that are easier to scale, secure, and operate consistently.',
        ctaText: 'Tame My Cluster',
        relatedArticles: [
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Network Security Foundations for Cloud-Native Systems', url: '/articles/network-security-foundations' },
            { title: 'Platform Modernization Program', url: '/articles/platform-modernization-program' },
            { title: 'Infrastructure Strategy Notes', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('aws.svg'),
            serviceTag('azure.svg'),
            serviceTag('gcp.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('terraform.svg'),
            serviceTag('prometheus-svgrepo-com.svg'),
            serviceTag('grafana.svg'),
            serviceTag('openshift-svgrepo-com.svg'),
            serviceTag('openstack-icon-svgrepo-com.svg'),
            serviceTag('cloudflare.svg'),
            serviceTag('ansible-svgrepo-com.svg'),
            serviceTag('ubuntu-svgrepo-com.svg'),
            serviceTag('debian-svgrepo-com.svg'),
            serviceTag('redhat-icon-svgrepo-com.svg'),
            serviceTag('archlinux-svgrepo-com.svg'),
            serviceTag('linode.svg'),
            serviceTag('datadog-svgrepo-com.svg'),
            serviceTag('splunk-svgrepo-com.svg'),
            serviceTag('gitlab-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <AnsiblePlaybookText/>,
        ExpandedServiceCardContent: () => <Suspense fallback={null}><ComplianceAnimation/></Suspense>,
    },
    {
        title: 'Cyber',
        shortDescription: 'Security engineering for modern systems, cloud estates, and digital operations.',
        fullDescription: 'We design secure systems with layered controls, identity-aware boundaries, and practical operating procedures so teams can ship with confidence. Our approach focuses on hardening the architecture, reducing attack surface, and making security measurable rather than theoretical.',
        ctaText: 'Close The Backdoor',
        relatedArticles: [
            { title: 'Network Security Foundations for Cloud-Native Systems', url: '/articles/network-security-foundations' },
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Secure Delivery Playbooks', url: '/articles/coming-soon' },
            { title: 'Threat Modeling in Practice', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('bitwarden.svg'),
            serviceTag('cloudflare.svg'),
            serviceTag('splunk-svgrepo-com.svg'),
            serviceTag('datadog-svgrepo-com.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('docker.svg'),
            serviceTag('aws.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={(p) => keyCrack(p, 16)}/>,
        ExpandedServiceCardContent: () => <Suspense fallback={null}><AnimalClassification/></Suspense>,
    },
    {
        title: 'Graphics & Rendering',
        shortDescription: 'High-performance visuals, interactive tools, and GPU-accelerated experiences.',
        fullDescription: 'We deliver rendering-heavy experiences for simulation, creative tooling, and product visualisation with a strong focus on performance and runtime stability. The work spans real-time graphics, graphics APIs, and cross-platform execution where frame-time and memory efficiency matter.',
        ctaText: 'Melt The GPU',
        relatedArticles: [
            { title: 'Vulkan Compute Pipelines', url: '/articles/vulkan-compute-pipelines' },
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Rendering at Scale', url: '/articles/coming-soon' },
            { title: 'Performance Tuning Notes', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('blender-svgrepo-com.svg'),
            serviceTag('figma-svgrepo-com.svg'),
            serviceTag('opencv-svgrepo-com.svg'),
            serviceTag('opengl-svgrepo-com.svg'),
            serviceTag('vulkan-svgrepo-com.svg'),
            serviceTag('cpp.svg'),
            serviceTag('csharp.svg'),
            serviceTag('python.svg'),
            serviceTag('javascript.svg'),
            serviceTag('typescript.svg'),
            serviceTag('swift.svg'),
            serviceTag('react-native.svg'),
            serviceTag('tensorflow.svg'),
            serviceTag('aws.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('nvidea.svg'),
            serviceTag('macos-svgrepo-com.svg'),
            serviceTag('microsoft-windows-svgrepo-com.svg'),
            serviceTag('ubuntu-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={(p) => wireframe(p, 'cube')}/>,
        ExpandedServiceCardContent: () => <Suspense fallback={null}><AnimalClassification/></Suspense>,
    },
    {
        title: 'Embedded & Systems',
        shortDescription: 'Firmware, device software, and dependable low-level systems integration.',
        fullDescription: 'We build embedded and systems software for constrained hardware, custom devices, and operational technology where reliability matters more than abstraction. The work covers communication layers, boot workflows, device integration, and robust interfaces between edge systems and platform services.',
        ctaText: 'Flash The Metal',
        relatedArticles: [
            { title: 'Vulkan Compute Pipelines', url: '/articles/vulkan-compute-pipelines' },
            { title: 'Network Security Foundations for Cloud-Native Systems', url: '/articles/network-security-foundations' },
            { title: 'Embedded Reliability Patterns', url: '/articles/coming-soon' },
            { title: 'Device Integration Case Study', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('cpp.svg'),
            serviceTag('csharp.svg'),
            serviceTag('golang.svg'),
            serviceTag('lua.svg'),
            serviceTag('java-svgrepo-com.svg'),
            serviceTag('kotlin-svgrepo-com.svg'),
            serviceTag('swift.svg'),
            serviceTag('ubuntu-svgrepo-com.svg'),
            serviceTag('debian-svgrepo-com.svg'),
            serviceTag('archlinux-svgrepo-com.svg'),
            serviceTag('centos-svgrepo-com.svg'),
            serviceTag('freebsd-svgrepo-com.svg'),
            serviceTag('microsoft-windows-svgrepo-com.svg'),
            serviceTag('macos-svgrepo-com.svg'),
            serviceTag('gnu-svgrepo-com.svg'),
            serviceTag('homebrew-svgrepo-com.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('ansible-svgrepo-com.svg'),
            serviceTag('terraform.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={pinMux}/>,
        ExpandedServiceCardContent: forwardRef<number, {}>((_, ref) => {

            const [characters, setCharacters] = useState(0)

            const {
                Keyword,
                Operator,
                Typename,
                Variable,
                Comment,
                RedactedComment,
                RedactedVariable,
                RedactedTemplateParam,
                RedactedTypename,
                CodeBlock
            } = createAnimatedCodeBlock(
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID_DARK,
                PROJECT_THEME.colour.COLOUR_DARK,
                PROJECT_THEME.colour.COLOUR_MID,
                PROJECT_THEME.colour.COLOUR_LIGHT,
                PROJECT_THEME.colour.COLOUR_MID_LIGHT,
                characters,
                60,
                100,
                setCharacters
            )

            return (
                <CodeBlock>
                    <Comment>
                        //
                    </Comment>
                    <RedactedComment>
                        SomeComment
                    </RedactedComment>
                    <RedactedComment>
                        Another
                    </RedactedComment>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Keyword>
                        &emsp;typename
                    </Keyword>
                    <RedactedTypename style={{marginRight: '0'}}>
                        sometypename
                    </RedactedTypename>
                    <Operator>
                        ,
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Keyword>
                        typename
                    </Keyword>
                    <RedactedTypename>
                        typename
                    </RedactedTypename>
                    <Operator>
                        =
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        std
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        enable_if_t
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        is_arithmetic_v
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        +(
                    </Operator>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Keyword>
                        return
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (*
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator>
                        ) +
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        type
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        &#123;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        some
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#125;;
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                    <br/>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        template
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Typename>
                        &emsp;size_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        sometypename
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <Typename>
                        uint64_t
                    </Typename>
                    <RedactedVariable style={{marginRight: '0'}}>
                        typename123
                    </RedactedVariable>
                    <br/>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        param
                    </RedactedTemplateParam>
                    <Operator style={{marginRight: '0'}}>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        lon
                    </RedactedTemplateParam>
                    <Operator>
                        &gt;
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        operator
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        *(
                    </Operator>
                    <Keyword>
                        const
                    </Keyword>
                    <RedactedTypename>
                        varx
                    </RedactedTypename>
                    <Operator style={{marginRight: '2px'}}>
                        &
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        somevariablename
                    </RedactedVariable>
                    <Operator>
                        )
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        const
                    </Keyword>
                    <br/>
                    <Operator>
                        &#123;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedTypename style={{marginRight: '0'}}>
                        fixed
                    </RedactedTypename>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <br/>
                    <Variable style={{marginRight: '0'}}>
                        &emsp;&emsp;std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        param1
                    </RedactedVariable>
                    <Operator>
                        ),
                    </Operator>
                    <br/>
                    <Variable style={{marginRight: '0'}}>
                        &emsp;&emsp;std
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        ::
                    </Operator>
                    <Variable style={{marginRight: '0'}}>
                        max
                    </Variable>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        anotherpara
                    </RedactedTemplateParam>
                    <Operator>
                        ,
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        alongerpara
                    </RedactedTemplateParam>
                    <Operator>
                        )
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&gt;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        &#123;&#125;;
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        =
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        &emsp;&emsp;static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        decltype
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variablename
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        )&gt;(
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <Keyword style={{marginRight: '0'}}>
                        this
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        -&gt;
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        ) *
                    </Operator>
                    <br/>
                    <Operator style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;(
                    </Operator>
                    <br/>
                    <Keyword style={{marginRight: '0'}}>
                        &emsp;&emsp;&emsp;&emsp;static_cast
                    </Keyword>
                    <Operator style={{marginRight: '0'}}>
                        &lt;
                    </Operator>
                    <Typename style={{marginRight: '0'}}>
                        long double
                    </Typename>
                    <Operator style={{marginRight: '0'}}>
                        &gt;
                    </Operator>
                    <Operator style={{marginRight: '0'}}>
                        (
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        variable
                    </RedactedVariable>
                    <Operator style={{marginRight: '0'}}>
                        .
                    </Operator>
                    <RedactedVariable style={{marginRight: '0'}}>
                        data
                    </RedactedVariable>
                    <Operator>
                        ) /
                    </Operator>
                    <br/>
                    <Operator>
                        &emsp;&emsp;&emsp;
                    </Operator>
                    <RedactedTemplateParam style={{marginRight: '0'}}>
                        somethingprettylong
                    </RedactedTemplateParam>
                    <br/>
                    <Operator>
                        &emsp;&emsp;&emsp;)
                    </Operator>
                    <br/>
                    <Operator>
                        &emsp;&emsp;);
                    </Operator>
                    <br/>
                    <Operator>
                        &#125;
                    </Operator>
                </CodeBlock>
            )
        }),
    },
    {
        title: 'Data Engineering',
        shortDescription: 'Reliable data pipelines, integration layers, and analytics platforms.',
        fullDescription: 'We design data systems that move from raw events to trusted decision-making services without losing observability or control. Our approach focuses on scalable ingestion, robust contracts, and operational clarity so teams can rely on their data instead of fighting brittle infrastructure.',
        ctaText: 'Ingest & Wrangle',
        relatedArticles: [
            { title: 'RAG - Need Some Context!', url: '/articles/rag' },
            { title: 'Kubernetes Scaling - From Monolith to Elastic Services', url: '/articles/kubernetes-monolith-scaling' },
            { title: 'Data Platform Foundations', url: '/articles/coming-soon' },
            { title: 'Observability for Data Systems', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('kafka.svg'),
            serviceTag('postgresql.svg'),
            serviceTag('mongo-svgrepo-com.svg'),
            serviceTag('mysql-svgrepo-com.svg'),
            serviceTag('mariadb.svg'),
            serviceTag('elasticsearch.svg'),
            serviceTag('hadoop-distributed-file-system.svg'),
            serviceTag('python.svg'),
            serviceTag('scala-svgrepo-com(1).svg'),
            serviceTag('java-svgrepo-com.svg'),
            serviceTag('aws.svg'),
            serviceTag('azure.svg'),
            serviceTag('gcp.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('terraform.svg'),
            serviceTag('grafana.svg'),
            serviceTag('prometheus-svgrepo-com.svg'),
            serviceTag('datadog-svgrepo-com.svg'),
            serviceTag('splunk-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={beeswarm}/>,
        ExpandedServiceCardContent: () => <Suspense fallback={null}><AnimalClassification/></Suspense>,
    },
    {
        title: 'Machine Learning',
        shortDescription: 'Applied ML products, model delivery, and production-ready intelligent features.',
        fullDescription: 'We help teams turn machine learning ideas into dependable product capabilities with clear evaluation, thoughtful integrations, and operational guardrails. The work spans experimentation, deployment, monitoring, and the product decisions that make intelligent features genuinely useful in the real world.',
        ctaText: 'Build A Brain',
        relatedArticles: [
            { title: 'RAG - Need Some Context!', url: '/articles/rag' },
            { title: 'Vulkan Compute Pipelines', url: '/articles/vulkan-compute-pipelines' },
            { title: 'ML Delivery Checklist', url: '/articles/coming-soon' },
            { title: 'Model Operations Notes', url: '/articles/coming-soon' },
        ],
        tags: [
            serviceTag('deepseek.svg'),
            serviceTag('python.svg'),
            serviceTag('tensorflow.svg'),
            serviceTag('opencv-svgrepo-com.svg'),
            serviceTag('jupyter-svgrepo-com.svg'),
            serviceTag('kafka.svg'),
            serviceTag('postgresql.svg'),
            serviceTag('mongo-svgrepo-com.svg'),
            serviceTag('elasticsearch.svg'),
            serviceTag('aws.svg'),
            serviceTag('azure.svg'),
            serviceTag('gcp.svg'),
            serviceTag('docker.svg'),
            serviceTag('kubernetes.svg'),
            serviceTag('cpp.svg'),
            serviceTag('golang.svg'),
            serviceTag('java-svgrepo-com.svg'),
            serviceTag('scala-svgrepo-com(1).svg'),
            serviceTag('grafana.svg'),
            serviceTag('prometheus-svgrepo-com.svg'),
        ],
        ServiceCardContent: () => <ServiceAnimation build={(p) => attentionFlow(p, TRANSFORMER_TOKENS)}/>,
        ExpandedServiceCardContent: () => <Suspense fallback={null}><AnimalClassification/></Suspense>,
    }

// Web,
// Graphics & Rendering
// Cloud & Platform, Enterprise Applications
// Machine Learning, Embedded & Systems
// CyberSecurity, Data Science

]

export default SERVICES